import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Contact } from '@lockton-apps/shared/interfaces';
import { ClientContactsActions } from '@lockton-apps/clients/actions';
import {
  ContactCreateUpdateActions,
  ContactsListActions,
} from '@lockton-apps/contacts/action';
import { LsosContactActions } from '@lockton-apps/lsos-renewals/action';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import { ContactsApiActions } from '@lockton-apps/shared/action/contacts';

export const CONTACTS_FEATURE_KEY = 'contacts';

export interface ContactsState extends EntityState<Contact> {
  selectedId?: string | null; // which Contacts record has been selected
  loaded: boolean; // has the Contacts list been loaded
  mutationsLoading: boolean;
  error?: unknown; // last known error (if any)
}

export interface ContactsPartialState {
  readonly [CONTACTS_FEATURE_KEY]: ContactsState;
}

export const contactsAdapter: EntityAdapter<Contact> =
  createEntityAdapter<Contact>({ selectId: (contact) => contact.userId });

export const initialContactsState: ContactsState =
  contactsAdapter.getInitialState({
    // set initial required properties
    loaded: false,
    mutationsLoading: false,
  });

const reducer = createReducer(
  initialContactsState,
  on(
    ClientContactsActions.init,
    ContactCreateUpdateActions.init,
    PolicyDetailsActions.init,
    ContactsListActions.init,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(
    ContactsApiActions.loadContactsSuccess,
    ContactsApiActions.loadClientContactsSuccess,
    (state, { contacts }) =>
      contactsAdapter.upsertMany(contacts, {
        ...state,
        loaded: true,
      })
  ),
  on(ContactsApiActions.loadClientContactsFailure, (state, { error }) => ({
    ...state,
    loaded: true,
    error,
  })),
  on(
    ContactsApiActions.loadContactSuccess,
    ContactsApiActions.loadContactSuccessThenLoadClient,
    ContactsApiActions.loadAuthenticatedContactSuccess,
    (state, { contact }) =>
      contactsAdapter.setOne(contact, {
        ...state,
        loaded: true,
      })
  ),
  on(
    ClientContactsActions.contactEnabled,
    ContactsListActions.contactEnabled,
    ContactsApiActions.disabledFailure,
    (state, { userId }) =>
      contactsAdapter.mapOne(
        { id: userId, map: (contact) => ({ ...contact, enabled: true }) },
        state
      )
  ),
  on(
    ContactsApiActions.disabledSuccess,
    ContactsApiActions.enabledFailure,
    (state, { userId }) =>
      contactsAdapter.mapOne(
        { id: userId, map: (contact) => ({ ...contact, enabled: false }) },
        state
      )
  ),
  on(
    ContactCreateUpdateActions.contactAdded,
    ContactCreateUpdateActions.contactUpdated,
    LsosContactActions.addContact,
    (state) => ({
      ...state,
      mutationsLoading: true,
    })
  ),
  on(
    ContactsApiActions.addSuccess,
    ContactsApiActions.addPrimaryClientContactSuccess,
    (state, { contact }) =>
      contactsAdapter.addOne(contact, {
        ...state,
        mutationsLoading: false,
      })
  ),
  on(
    ContactsApiActions.addFailure,
    ContactsApiActions.updateContactFailure,
    (state, { error }) => ({
      ...state,
      mutationsLoading: false,
      error,
    })
  ),
  on(ContactsApiActions.updateContactSuccess, (state, { contact }) =>
    contactsAdapter.upsertOne(contact, {
      ...state,
      mutationsLoading: false,
    })
  )
);

export function contactsReducer(
  state: ContactsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
